import type { ReactNode } from "react";

import { cn } from "../../lib/cn";
import type { Tone } from "./tone";

const toneClassNames: Record<Tone, string> = {
	neutral: "bg-surface-2 text-ink-2",
	ok: "bg-ok-soft text-ok",
	warn: "bg-warn-soft text-warn",
	alert: "bg-alert-soft text-alert",
	info: "bg-info-soft text-info",
};

const dotClassNames: Record<Tone, string> = {
	neutral: "bg-ink-3",
	ok: "bg-ok",
	warn: "bg-warn",
	alert: "bg-alert",
	info: "bg-info",
};

const ringClassNames: Record<Tone, string> = {
	neutral: "border-line",
	ok: "border-ok/30",
	warn: "border-warn/30",
	alert: "border-alert/30",
	info: "border-info/30",
};

export interface PillProps {
	readonly tone?: Tone;
	readonly children: ReactNode;
}

/** Status pill: 11px, dot-prefixed, fully round. */
export function Pill({ tone = "neutral", children }: PillProps) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-[5px] rounded-pill py-[2px] pr-2 pl-[7px] text-tiny font-medium",
				toneClassNames[tone],
			)}
		>
			<span aria-hidden="true" className={cn("h-[5px] w-[5px] shrink-0 rounded-full", dotClassNames[tone])} />
			{children}
		</span>
	);
}

/** Badge: 10.5px uppercase, outlined — for taxonomy, not status. */
export function Badge({ tone = "neutral", children }: PillProps) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-[10px] border px-2 py-[2px] text-label leading-[1.4] font-semibold tracking-[0.02em] whitespace-nowrap uppercase",
				toneClassNames[tone],
				ringClassNames[tone],
			)}
		>
			{children}
		</span>
	);
}
